import '~/assets/styles/index.scss';

import React from 'react';
import { GeoLocationProvider } from '~/app/context';
import { ConsentProvider, resolveConsent } from '~/app/consent';
import { CookieConsentPanel } from '~/widgets/cookie-consent/cookie-consent-panel';

const ATTR_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'cq_cmp',
  'cq_con',
  'cq_med',
  'cq_net',
  'cq_plt',
  'cq_src',
  'cq_term',
  'city',
];

const GTM_CODE = process.env.GTM_CODE;
const TOUCH_STORAGE = {
  first: 'traffic_first_touch',
  last: 'traffic_last_touch',
};
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

let attributionInitialized = false;
let gtmInitialized = false;

export const wrapRootElement = ({ element }) => {
  const ssrLocation =
    typeof document !== 'undefined' ? document.querySelector('[data-location]')?.getAttribute('data-location') || '' : '';
  return (
    <ConsentProvider>
      <GeoLocationProvider ssrLocation={ssrLocation}>
        {element}
        <CookieConsentPanel />
      </GeoLocationProvider>
    </ConsentProvider>
  );
};

const getCookie = (name) => {
  if (typeof document === 'undefined') {
    return '';
  }
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop().split(';').shift() : '';
};

const setCookie = (name, value, maxAge = COOKIE_MAX_AGE_SECONDS) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; samesite=lax`;
};

const safeJsonParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
};

const readParams = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const data = {};
  ATTR_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      data[key] = value;
    }
  });

  return data;
};

const buildTouchPayload = (params) => {
  if (typeof window === 'undefined') {
    return {};
  }
  return {
    ...params,
    page_url: window.location.href,
    page_path: window.location.pathname,
    referrer: document.referrer || '',
    timestamp: new Date().toISOString(),
  };
};

const persistAttribution = () => {
  if (typeof window === 'undefined' || attributionInitialized) {
    return;
  }
  attributionInitialized = true;

  try {
    const params = readParams();
    const touchPayload = buildTouchPayload(params);

    const firstTouchStored = window.localStorage.getItem(TOUCH_STORAGE.first);
    const firstTouch = firstTouchStored ? safeJsonParse(firstTouchStored, touchPayload) : touchPayload;
    const lastTouch = touchPayload;

    if (!firstTouchStored) {
      window.localStorage.setItem(TOUCH_STORAGE.first, JSON.stringify(firstTouch));
    }
    window.localStorage.setItem(TOUCH_STORAGE.last, JSON.stringify(lastTouch));

    // Attribution cookies feed marketing/ad tooling (CTM); only write them with ads consent.
    if (resolveConsent().ads) {
      Object.entries(firstTouch).forEach(([key, value]) => {
        setCookie(`ctm_attr_first_${key}`, String(value));
      });
      Object.entries(lastTouch).forEach(([key, value]) => {
        setCookie(`ctm_attr_last_${key}`, String(value));
      });
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'traffic_context_ready',
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || '',
      source_data: { first_touch: firstTouch, last_touch: lastTouch },
      first_touch: firstTouch,
      last_touch: lastTouch,
    });
  } catch (_) {
    // Ignore storage errors (privacy mode / blocked storage).
  }
};

const loadGtm = () => {
  if (typeof window === 'undefined' || !GTM_CODE || gtmInitialized) {
    return;
  }

  gtmInitialized = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CODE}`;
  document.head.appendChild(script);
};

// CallTrackingMetrics performs dynamic number swap for ad attribution; only run it with ads consent.
const runCtmWhenReady = () => {
  if (typeof window === 'undefined' || !resolveConsent().ads) {
    return;
  }
  const tryRun = (attempt = 0) => {
    if (window.__ctm?.main?.runNow) {
      window.__ctm.main.runNow();
      return;
    }
    if (attempt < 12) {
      setTimeout(() => tryRun(attempt + 1), 250);
    }
  };
  tryRun();
};

const installDeferredGtmLoader = () => {
  if (typeof window === 'undefined' || !GTM_CODE) {
    return;
  }

  let armed = true;
  const passiveOptions = { passive: true };

  const trigger = () => {
    if (!armed) {
      return;
    }
    armed = false;
    loadGtm();
    window.removeEventListener('scroll', trigger, passiveOptions);
    window.removeEventListener('pointerdown', trigger, passiveOptions);
    window.removeEventListener('keydown', trigger);
  };

  window.addEventListener('scroll', trigger, passiveOptions);
  window.addEventListener('pointerdown', trigger, passiveOptions);
  window.addEventListener('keydown', trigger);

  const isPaidTraffic = ['gclid', 'fbclid', 'msclkid', 'utm_source', 'utm_campaign'].some((key) =>
    new URLSearchParams(window.location.search).has(key)
  );

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => trigger(), { timeout: isPaidTraffic ? 800 : 2000 });
  }
  setTimeout(trigger, isPaidTraffic ? 1200 : 2800);
};

const generateEventId = () => `_lp_${Math.random().toString(36).slice(2, 11)}`;

// Sends a Meta Conversions API event (fbc/fbp + Lead/PageView); this is ad measurement, so it requires ads consent.
const sendTrackEvent = (event_name) => {
  if (typeof window === 'undefined' || !resolveConsent().ads) {
    return;
  }

  const event_id = generateEventId();
  fetch('/api/trackEvent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event_name,
      event_id,
      event_source_url: window.location.href,
      client_user_agent: window.navigator.userAgent,
      fbc: getCookie('_fbc'),
      fbp: getCookie('_fbp'),
    }),
  }).catch(() => null);
};

const installPhoneClickTracking = () => {
  if (typeof document === 'undefined') {
    return;
  }
  document.addEventListener('click', (event) => {
    const anchor = event.target?.closest?.('a[href^="tel:"]');
    if (!anchor) {
      return;
    }

    sendTrackEvent('Lead');
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'phone_click',
        phone_href: anchor.getAttribute('href') || '',
      });
    }
  });
};

const HEADER_OFFSET = 100;

// Anything the visitor does on their own ends the correction below; otherwise it
// keeps pulling the page back to the footer, e.g. over the scroll-to-top button.
const USER_SCROLL_INTENT_EVENTS = ['pointerdown', 'touchstart', 'wheel', 'keydown'];

let cancelHashScroll = () => {};

// The hero disclaimer links scroll down to the footer legal block. Images below
// the fold keep loading while the smooth scroll runs and push the footer further
// down, so the position is corrected until it stops moving.
const scrollToHash = (id) => {
  cancelHashScroll();

  let settled = 0;
  let timeoutId = null;
  let cancelled = false;

  const cancel = () => {
    cancelled = true;
    clearTimeout(timeoutId);
    USER_SCROLL_INTENT_EVENTS.forEach((type) => window.removeEventListener(type, cancel));
  };

  cancelHashScroll = cancel;
  USER_SCROLL_INTENT_EVENTS.forEach((type) => window.addEventListener(type, cancel, { passive: true }));

  const step = (attempt) => {
    if (cancelled) {
      return;
    }

    const element = document.getElementById(id);
    if (!element) {
      cancel();
      return;
    }

    const top = Math.max(0, element.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET);
    const drift = Math.abs(top - window.pageYOffset);

    if (drift < 4) {
      settled += 1;
    } else {
      settled = 0;
      window.scrollTo({ top, behavior: attempt === 0 ? 'smooth' : 'auto' });
    }

    if (settled < 2 && attempt < 20) {
      timeoutId = setTimeout(() => step(attempt + 1), attempt === 0 ? 600 : 150);
    } else {
      cancel();
    }
  };

  step(0);
};

const installHashLinkScrolling = () => {
  if (typeof document === 'undefined') {
    return;
  }

  document.addEventListener('click', (event) => {
    const link = event.target?.closest?.('a[href^="#"]');
    const href = link?.getAttribute('href');
    if (!href || href === '#') {
      return;
    }

    const id = decodeURIComponent(href.slice(1));
    if (!document.getElementById(id)) {
      return;
    }

    event.preventDefault();
    scrollToHash(id);

    // Skip links target a focusable region (tabindex="-1"); move focus there
    // too, or the next Tab would start from the top of the page again.
    const destination = document.getElementById(id);
    if (destination.getAttribute('tabindex') === '-1') {
      destination.focus({ preventScroll: true });
    }
  });
};

if (typeof window !== 'undefined') {
  persistAttribution();
  installDeferredGtmLoader();
  installPhoneClickTracking();
  installHashLinkScrolling();
  runCtmWhenReady();

  // When ads consent is newly granted (Accept All / Save) without a page reload,
  // re-run the ads-gated side effects immediately instead of waiting for the next visit.
  window.addEventListener('drgd:consent-changed', (event) => {
    if (event.detail?.ads) {
      attributionInitialized = false;
      persistAttribution();
      runCtmWhenReady();
    }
  });
}

export const onRouteUpdate = () => {
  persistAttribution();
  sendTrackEvent('PageView');
  runCtmWhenReady();
};
